import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { DashboardFilterValue, DataRange, ExportType } from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import {
  ADMIN_DASHBOARD_VIEW,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { BlockEmailsBanner } from '@/components/block-emails';
import { ProductActivityOverTime } from '@/components/courses/ProductActivityOverTime';
import {
  AdminDashboardLayoutV2,
  AdminTasks,
  AwarenessTraining,
  CustomerBranding,
  EmptyProductCard,
  PhishingProduct,
} from '@/components/dashboards/admin';
import { ExportContext } from '@/components/reports/ReportMessage';
import { useAccountAdminTasks } from '@/hooks/query/account-admin-tasks.hooks';
import { useAppSelector } from '@/hooks/store';
import { useAwarenessHidden } from '@/hooks/useAwarenessHidden';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import dashboardAdmin from '@/images/placeholders/dashboard-admin.svg';
import { SCMainDashboardV2 } from '@/pages/security-culture';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { AccordionTabHeader } from '@/ui/accordion';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoModeActive } from '@/utils/demo-mode';
import {
  branchAdminCheck,
  dashboardDataRangeOptions,
  getAccountIntegration,
  getErrorMessageFromADSync,
  ProductType,
  showBlockEmailsMessage,
} from '@/utils/helpers';
import { getHubspotIsActiveStatus, hubspotTrack } from '@/utils/hubspot';
import { AxiosError } from 'axios';
import { AccordionTab, AccordionTabChangeEvent } from 'primereact/accordion';
import { Message } from 'primereact/message';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  align-items: center;
  justify-content: center;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);

  img {
    width: 300px;
  }

  p {
    padding: 0;
    margin: 0;
    line-height: var(--medium-line-height);
    font-weight: 500;
    text-align: center;
  }

  h1 {
    text-align: center;
    margin-top: var(--default-padding);
    margin-bottom: var(--small-padding);
  }

  #admin-dashboard {
    grid-area: ac;
  }
`;

export const AdminDashboardPageV2 = () => {
  const { t } = useTranslation();
  const { cannot } = usePermission();
  const account = useAppSelector(selectCurrentAccount);
  const { user } = useAppSelector(selectCurrentUserState);
  const navigate = useNavigate();
  const toast = useToast();
  const {
    setType,
    setExportKey,
    setIsDownloading,
    setNotificationParam,
    isConnected,
    isDownloading,
  } = useContext(ExportContext);

  // TODO: add set function later
  const [dataRange] = useState<DataRange>(dashboardDataRangeOptions(t)[2]);
  const [filterData] = useState<DashboardFilterValue>({
    managers: [],
    departments: [],
    branches: [],
    groups: [],
  });

  const isDemoAccount = account?.name === 'Demo Company';
  const isFreeTrial = !!account?.freeTrialEndsAt;
  const isAwarenessHidden = useAwarenessHidden(account);

  const activeAwarenessTraining =
    !isAwarenessHidden &&
    (getHubspotIsActiveStatus(account?.hubspot?.awtStatus) ||
      isDemoAccount ||
      isFreeTrial);

  const activePhishing =
    getHubspotIsActiveStatus(account?.hubspot?.phishingStatus) ||
    getHubspotIsActiveStatus(account?.hubspot?.autoPhishingStatus) ||
    getHubspotIsActiveStatus(account?.hubspot?.phishingOneOffStatus) ||
    isDemoAccount ||
    isFreeTrial;

  const activeSecurityCulture =
    getHubspotIsActiveStatus(account?.hubspot?.securityCultureStatus) ||
    isDemoAccount ||
    isFreeTrial;

  const [surveyId, setSurveyId] = useState<string>();

  const withoutActiveProduct =
    !activeAwarenessTraining && !activePhishing && !activeSecurityCulture;

  const getFilterData = (
    filterData: DashboardFilterValue,
    dataRange: DataRange,
  ) => {
    return {
      ...dataRange.value,
      managers: filterData.managers?.map((manager) => manager.name),
      departments: filterData.departments?.map((department) => department.name),
      branches: filterData.branches?.map((branch) => branch.id),
      groups: filterData.groups?.map((group) => group.id),
    };
  };

  const { accountAdminTasks, isLoading: areAdminTasksLoading } =
    useAccountAdminTasks(account?.id as string, {
      take: 0,
      skip: 0,
    });

  useEffect(() => {
    hubspotTrack(HubspotProperty.ADMIN_DASHBOARD_OPENED, HubspotValue.YES);
  }, []);

  const handleActiveIndexChange = (e: AccordionTabChangeEvent) => {
    const newIndex = e.index as number[];
    setActiveIndex(newIndex);
  };

  const isAdminTaskFeatureEnabled =
    !areAdminTasksLoading && !!accountAdminTasks?.count && !isDemoModeActive();

  // Accordion state management
  const activeIndexesKey = `${account?.id}-${user?.id}`;
  const getDefaultIndexes = () => {
    const indexes = [0, 1, 2, 3];
    if (isAdminTaskFeatureEnabled) indexes.push(4);
    return indexes;
  };
  const defaultIndexes = getDefaultIndexes();

  let storedActiveIndexes: Record<string, number[]> = {};
  const storedData = localStorage.getItem(ADMIN_DASHBOARD_VIEW);
  if (storedData) {
    storedActiveIndexes = JSON.parse(storedData);
  }

  const preselectedActiveIndex = storedActiveIndexes[activeIndexesKey]?.length
    ? storedActiveIndexes[activeIndexesKey]
    : defaultIndexes;

  const [activeIndex, setActiveIndex] = useState<number[]>(
    preselectedActiveIndex,
  );

  useEffect(() => {
    localStorage.setItem(
      ADMIN_DASHBOARD_VIEW,
      JSON.stringify({
        [activeIndexesKey]: activeIndex,
      }),
    );
  }, [JSON.stringify(activeIndex), activeIndexesKey]);

  const currentAccountIntegration = getAccountIntegration(account);
  const isBranchAdmin = branchAdminCheck(user, account);

  if (cannot(Actions.READ, Subjects.ADMIN_DASHBOARD) || !account) {
    return (
      <FlexContainer height="100%" direction="column" gap={24}>
        <h1>{t('profile.dashboard.denied')}</h1>
      </FlexContainer>
    );
  }

  const handleErrorLogRedirect = () => {
    navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]());
  };

  const handleExportExecutiveSummary = useCallback(async () => {
    const exportKey = uuidv4();

    setType(ExportType.EXECUTIVE_SUMMARY);
    setExportKey(exportKey);
    setNotificationParam(account.id);
    setIsDownloading(true);
    await new Promise<void>((resolve) => {
      const deadline = Date.now() + 10000;
      const intervalId = setInterval(() => {
        if (isConnected?.current || Date.now() > deadline) {
          clearInterval(intervalId);
          resolve();
        }
      }, 100);
    });
    try {
      if (isConnected?.current) {
        await client.reports.exportExecutiveSummary(account.id, { exportKey });
      } else {
        setIsDownloading(false);
      }
    } catch (e) {
      setIsDownloading(false);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  }, [
    account?.id,
    setType,
    setExportKey,
    setNotificationParam,
    setIsDownloading,
    isConnected,
  ]);

  if (account?.isSystem) {
    return (
      <FlexContainer height="auto" minHeight="100%" align="stretch">
        <StyledDiv>
          <img src={dashboardAdmin} />
          <h1>{t('profile.welcome.admin.heading')}</h1>
          <p>
            {t('profile.welcome.admin.p1')}
            <br /> {t('profile.welcome.admin.p2')}
          </p>
        </StyledDiv>
      </FlexContainer>
    );
  }

  const pathItems = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('generic.dashboard'),
      url: RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const hasSyncMessage =
    !!currentAccountIntegration?.lastSyncErrorLog &&
    !!getErrorMessageFromADSync(
      currentAccountIntegration?.lastSyncErrorLog,
      t,
    ) &&
    !isBranchAdmin;

  const hasBlockEmailsBanner = showBlockEmailsMessage(account);

  return (
    <>
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <AppBreadCrumb model={pathItems} />
      <FlexContainer
        justify="space-between"
        align="center"
        className="mb-5"
        wrap="wrap"
        gap={12}
      >
        <h1 className="mb-0">{t('generic.dashboard')}</h1>
        <FlexContainer justify="flex-end">
          <AppButton
            label={t('dashboard.exportExecutiveSummary')}
            type="outlined"
            icon="pi pi-download"
            iconPos="right"
            onClick={handleExportExecutiveSummary}
            aria-label={t('dashboard.exportExecutiveSummary')}
            isDisabled={isDemoModeActive()}
            state={
              isDownloading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
            }
          />
        </FlexContainer>
      </FlexContainer>
      <AdminDashboardLayoutV2
        hasSyncMessage={hasSyncMessage}
        isAdminTaskFeatureEnabled={isAdminTaskFeatureEnabled}
      >
        <CustomerBranding />
        {hasSyncMessage && (
          <Message
            id="sync-error-message"
            severity="warn"
            text={t('sync.error.dashboard.admin.message')}
            onClick={handleErrorLogRedirect}
            style={{ cursor: 'pointer' }}
          />
        )}

        {isAdminTaskFeatureEnabled && <AdminTasks className="admin-tasks" />}
        {/* TODO: Enable later */}
        {/* <DashboardFilter
          dataRange={dataRange}
          setDataRange={setDataRange}
          setFilterData={setFilterData}
        /> */}

        {/* TODO: TBD how to filter this reports */}
        {/* <OverviewReportsV2
          accountId={account.id}
          filters={getFilterData(filterData, dataRange)}
          t={t}
          activeAwarenessTraining={activeAwarenessTraining}
          activePhishing={activePhishing}
          activeSecurityCulture={!!activeSecurityCulture}
        /> */}

        <AppAccordion
          id="admin-dashboard"
          activeIndex={activeIndex}
          onTabChange={handleActiveIndexChange}
          multiple
          style={{ width: '100%' }}
        >
          {!withoutActiveProduct && (
            <AccordionTab
              tabIndex={0}
              headerTemplate={
                <AccordionTabHeader
                  title={t('dashboard.activity.overTime')}
                  description={t('dashboard.activity.overTime.description')}
                />
              }
            >
              <ProductActivityOverTime
                filters={getFilterData(filterData, dataRange)}
                activeAwarenessTraining={activeAwarenessTraining}
                activePhishing={activePhishing}
                activeSecurityCulture={!!activeSecurityCulture}
                isMainDashboard
              />
            </AccordionTab>
          )}

          {!isAwarenessHidden && (
            <AccordionTab
              tabIndex={1}
              headerTemplate={
                activeAwarenessTraining ? (
                  <AccordionTabHeader
                    title={t('dashboard.awareness.title')}
                    description={t('dashboard.awareness.description')}
                    buttonLabel={t('dashboard.viewDashboard')}
                    buttonOnClick={() =>
                      navigate(
                        RedirectPaths[RedirectPathsEnum.CONTENT_ANALYTICS](),
                      )
                    }
                  />
                ) : (
                  <span className="inactive-product-header">
                    <AccordionTabHeader
                      title={t('dashboard.awareness.title')}
                    />
                  </span>
                )
              }
            >
              {activeAwarenessTraining ? (
                <AwarenessTraining
                  filters={getFilterData(filterData, dataRange)}
                />
              ) : (
                <EmptyProductCard
                  type={ProductType.AWARENESS}
                  id="empty-awareness-training"
                />
              )}
            </AccordionTab>
          )}

          <AccordionTab
            tabIndex={2}
            headerTemplate={
              activePhishing ? (
                <AccordionTabHeader
                  title={t('phishing')}
                  description={t('phishing.description')}
                  buttonLabel={t('dashboard.viewDashboard')}
                  buttonOnClick={() =>
                    navigate(
                      RedirectPaths[RedirectPathsEnum.PHISHING_ANALYTICS](),
                    )
                  }
                />
              ) : (
                <span className="inactive-product-header">
                  <AccordionTabHeader title={t('phishing')} />
                </span>
              )
            }
          >
            {activePhishing ? (
              <PhishingProduct filters={getFilterData(filterData, dataRange)} />
            ) : (
              <EmptyProductCard
                type={ProductType.PHISHING}
                id="empty-phishing-product"
              />
            )}
          </AccordionTab>

          <AccordionTab
            tabIndex={4}
            headerTemplate={
              activeSecurityCulture ? (
                <AccordionTabHeader
                  title={t('security.culture')}
                  description={t('security.culture.dashboard.desc')}
                  buttonLabel={t('dashboard.viewDashboard')}
                  buttonOnClick={() =>
                    navigate(
                      RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](
                        surveyId,
                      ),
                    )
                  }
                />
              ) : (
                <span className="inactive-product-header">
                  <AccordionTabHeader title={t('security.culture')} />
                </span>
              )
            }
          >
            {activeSecurityCulture ? (
              <SCMainDashboardV2 setSurveyId={setSurveyId} />
            ) : (
              <EmptyProductCard
                type={ProductType.SECURITY_CULTURE}
                id="empty-security-culture-product"
              />
            )}
          </AccordionTab>
        </AppAccordion>
      </AdminDashboardLayoutV2>
    </>
  );
};
